import { useLayoutEffect, useState } from 'react'

import { zoomOf } from 'utils/textZoom'

/**
 * Fail-safe height for the Labs page header block, used ONLY when the measurement
 * below has never run.
 *
 * 154 at 1280x800 — 134 for the title row plus the section tab row, plus
 * `LABS_HEADER_GAP`. It is not anybody's source of truth: the moment `LabsPage`'s
 * layout effect runs, the real measurement replaces it, and it is written as a CSS
 * `var()` fallback precisely so that a missing measurement degrades to *approximately
 * right* rather than to zero.
 *
 * ⚠️ Keep it honest — unlike the previous version of this constant, this value is the
 * behaviour when the publisher is absent, not a seed for one frame. It was 166, left
 * over from when `LABS_HEADER_GAP` was 8 (32px); the gap is now 5 (20px), so 134+20=154.
 */
export const LABS_HEADER_FALLBACK_H = 154

/**
 * Width of a Labs section's left column — the scope toggle, the search field and the
 * grouped index, in that order. Shared so the two sections cannot drift apart.
 *
 * 288, and it is DERIVED rather than picked. The widest template name is **"Purchase
 * recorded (scan to claim)" at 219.38px** in the rail's own font (15px/19.5px Lato),
 * measured with a rendered probe span across all 47 registry names after `document.fonts`
 * reported `loaded` — not estimated from character count. ("Transfer claim invite (no
 * account)" is one character LONGER and 3.5px narrower, which is why counting characters
 * would have picked the wrong name.)
 *
 * The rail costs 30px around that text — 22px of left inset (the `pl: 2` on the `SideNav`
 * wrapper plus the rail's own row padding) and 8px on the right — so the MINIMUM that fits
 * on one line is **219.38 + 30 = 250**. At 240 that name WRAPPED to two lines (measured:
 * 39px tall against the 20px single-line rows), which §49 does not allow. An earlier 256
 * cleared the minimum by 6.6px, i.e. one renamed template from wrapping again, silently.
 * 288 leaves **38px**.
 *
 * Re-measure if a longer name is added — 38px is slack, not immunity.
 */
export const LABS_TOC_W = 288

/**
 * The gap between the Labs section tab row and the top of the active section's content,
 * as `pb` on the header block itself.
 *
 * ## Why the gap lives HERE and not as `pt` on the content wrapper (VRG-560)
 *
 * It used to be `pt: 6` on the wrapper around `active.render()`, and that is what made
 * every section's own sticky sub-header VISIBLY SLIDE on scroll. Measured at 1280x800
 * before that change: the header block never moved (top 24 at every scroll position —
 * sticky resolves `top: 0` against the scroll container's padding box, so its 24px
 * padding is not a slide), but the section's own sticky element started at y=182 and
 * pinned at y=158. Exactly 24px of travel, and exactly the wrapper's `pt`.
 *
 * A sticky element whose natural position differs from its pinned position must slide the
 * difference — there is no way to pin it "already scrolled" while a gap sits above it.
 * So the gap lives INSIDE the measured header block: a section's sticky column has its
 * natural top AT the header block's bottom, which is precisely where it pins, and the
 * travel is zero.
 *
 * Sections with nothing sticky of their own keep an identical visual gap — the header
 * block's `pb` sits between the tab row and them either way.
 *
 * ⚠️ This is still load-bearing after the section BANDS were retired (VRG-560). The
 * sections no longer pin a horizontal band, but both now pin a left COLUMN at
 * `LABS_CONTENT_TOP`, and a column is subject to the identical slide: put this gap back
 * on the content wrapper as `pt` and the column travels that distance on the first
 * scroll. The thing that slides changed; the reason it slides did not.
 *
 * 5 (20px), NOT 8 (32px). "A little more air" was read as +8px and shipped at 32, which
 * the user rejected on sight as too much. Since nothing here scrolls away, every pixel is
 * spent on EVERY screen for the whole session — so the pinned case wants LESS padding
 * than the scrolling one did, not more. 20px reads as deliberate separation without
 * eating the fold.
 *
 * This governs every section, because they all read the one published offset — which is
 * the point of it living here.
 */
export const LABS_HEADER_GAP = 5

/**
 * ## The offset is published as a CSS custom property, NOT through React state (VRG-560)
 *
 * A section's own sticky band has to pin at the page header's bottom edge. Two stacked
 * sticky elements each resolve their `top` against the SCROLLPORT, never against each
 * other, so the second one's offset must literally be the first one's height — and that
 * height is only knowable at runtime.
 *
 * ### What this replaces, and why the replacement is not cosmetic
 *
 * It was a React context (`LabsHeaderOffsetContext`), measured by `LabsPage` and consumed
 * by each section as a number. **Its default was `0`** — while its own docblock claimed
 * the opposite, in as many words: *"The default is the pre-measurement fallback below,
 * not 0 — a section rendered before the first layout effect would otherwise pin flush to
 * the scrollport and flash under the title."* The code said `createContext(0)`. The
 * documentation described the intended behaviour; the program did the other thing.
 *
 * That gap matters because `0` is not a mildly-wrong offset, it is the worst possible
 * one: the band pins flush to the scrollport, i.e. underneath the full height of the
 * page header, and the tab row inside it is sliced off. Which is the exact symptom that
 * has now been reported three times on this surface.
 *
 * And a consumer CAN miss the provider. `createContext` mints a fresh context OBJECT per
 * module evaluation, so any time `LabsPage` and a section end up holding two different
 * evaluations of this module — a Fast Refresh / HMR update during a live editing session
 * is the everyday way that happens — the consumer silently falls back to the default
 * instead of seeing the Provider's value. There is no error and no warning; the band just
 * renders at `top: 0`. The published code being correct is not sufficient, because the
 * tab the user is looking at is not necessarily running one evaluation of it.
 *
 * ### Why a CSS variable removes the class rather than the instance
 *
 * - **It cannot resolve to 0.** Every read site spells its own fallback
 *   (`var(--labs-header-h, 154px)`), so "nobody published" degrades to approximately
 *   right instead of catastrophically wrong. This is the property the context could not
 *   have: a context default is one value chosen at definition time, and it was chosen
 *   wrong.
 * - **It has no identity to lose.** A custom property is matched by NAME through the
 *   cascade. Two module evaluations, a remount, a portal — none of them can desynchronise
 *   a publisher from a reader the way two context objects can.
 * - **It applies in the pass that measures it.** Writing the property is a style mutation
 *   inside the same layout effect that took the measurement, so the corrected `top` is in
 *   effect before the browser paints. Routing the number through `useState` meant every
 *   header height change — a web font landing, a `ReferrerBackLink` row appearing, the
 *   tab row gaining scroll buttons — spent at least one painted frame with the band
 *   pinned at the previous, smaller offset.
 *
 * The measurement itself is unchanged and still deliberate: see `usePublishedHeight`.
 * Hardcoding the number instead was rejected when the context was introduced and is
 * rejected again — a literal here went stale once already (`LABS_HEADER_H = 64` against a
 * real 93) and failed silently, as clipping that reads like a scrolling quirk.
 */
export const LABS_HEADER_H_VAR = '--labs-header-h'

/**
 * `top` for anything a section pins below the Labs page header — today each section's
 * left column.
 *
 * The `var()` fallback is load-bearing, not defensive noise — it is the difference
 * between "roughly right" and "hidden behind the header" when no publisher has run.
 *
 * ## There is exactly ONE offset now, and that is the point (VRG-560)
 *
 * This used to be `LABS_BAND_TOP`, and it had a sibling: `LABS_BELOW_BAND_TOP`, which
 * added a SECOND published height (`--labs-band-h`) for anything pinning below a
 * section's own sticky band as well as the page header. That two-level stack is the
 * geometry four separate commits fought — 663aa9615, d685e1fd8, ef214e4ac, 06f104ef0 —
 * because every fix had to hold for both levels at once: the band's `top`, the band's
 * `pb`, the column's `top`, and the column's height all had to agree, and any one of them
 * being derived differently from the others reintroduced a clip or a slide.
 *
 * The bands are gone. Their contents were FILTERS on the list below them (an App/Platform
 * scope, a Common/Platform/App scope), so they belong in the column beside that list, not
 * in a horizontal strip above everything. Moving them removed the second sticky level
 * outright rather than making it agree with the first — which is why this file no longer
 * publishes a band height, and why `LABS_BAND_H_VAR`, `LABS_BAND_FALLBACK_H`,
 * `LABS_BELOW_BAND_TOP` and `labsBelowBandHeight` do not exist.
 *
 * If a section ever genuinely needs a second sticky level, add it back deliberately and
 * read this note first: the cost is not the constant, it is that every offset downstream
 * of it becomes a sum that something has to keep in step.
 */
export const LABS_CONTENT_TOP = `var(${LABS_HEADER_H_VAR}, ${LABS_HEADER_FALLBACK_H}px)`

/**
 * Viewport-height for a sticky column pinned at `LABS_CONTENT_TOP`, less `inset` px of
 * slack.
 *
 * A sticky element TALLER than the room its containing block leaves is not merely tall —
 * it gets CLAMPED to that block's bottom edge and rides upward, which surfaces as the top
 * of the column disappearing behind the page header at the very end of the scroll, and
 * reads as a rendering glitch rather than a bug.
 *
 * Callers pass **48**, and both halves of it are the Layout's own 24px gutter (`p: 6`),
 * counted at each end: the TOP one because a sticky element pins to the SCROLLPORT, which
 * sits 24px down the viewport, so the column's pinned top is 24px lower on screen than
 * the offset it was given; the BOTTOM one to keep the column's foot off the scrollport's
 * bottom padding. Counting only one of them over-states the column by exactly 24px, which
 * is what produced the clamp described above.
 */
export const labsStickyColumnHeight = (inset: number) =>
  `calc(100vh - ${LABS_CONTENT_TOP} - ${inset}px)`

/**
 * Measure one element's height and publish it as `varName` on another, keeping it current
 * through resizes. Returns two **callback refs**: put `source` on the element to measure
 * and `target` on the ancestor the readers live under.
 *
 * `useLayoutEffect` + `ResizeObserver`: the first write lands before the browser paints
 * (so nothing is ever painted at the fallback), and the observer keeps it right through
 * window resizes, font loading, and a tab row that wraps or gains scroll buttons.
 *
 * The target is separate from the source because the reader is not inside the thing being
 * measured — the page header is a SIBLING of the content it offsets, so its height has to
 * be published onto a common ancestor for the cascade to carry it down.
 *
 * ### Why callback refs and not `useRef` + a deps array
 *
 * Both call sites mount their measured element LATE and conditionally: `LabsPage` renders
 * a `<Navigate>` instead of the header until `useCanAccess` resolves, and
 * `NotificationsLab` renders a skeleton instead of its band until the template list
 * loads. With object refs, the effect runs once against `null`, and nothing re-runs it
 * when the node finally attaches — so the property is never written and every reader
 * silently sits on its fallback. The only repair is remembering to thread the right
 * value into a deps array (`[loadingList]`, `[isAdmin]`), per call site, forever.
 *
 * A callback ref is invoked by React with the node itself, so storing it in state
 * re-triggers this effect exactly when the element attaches or detaches, with nothing to
 * remember. Given that this whole module exists because a stale-by-omission offset keeps
 * shipping, a mechanism with a "don't forget the dependency" step is not one to choose.
 */
export const usePublishedHeight = (varName: string) => {
  const [source, setSource] = useState<HTMLElement | null>(null)
  const [target, setTarget] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    if (!source || !target) return
    // LAYOUT px, not the painted `getBoundingClientRect().height`: the variable is read
    // back as a CSS length inside `#app`'s text zoom, which scales it again. A painted
    // height published here offset the sticky Labs columns by height × z² instead of
    // height × z (see `zoomOf` in utils/textZoom).
    const write = () =>
      target.style.setProperty(varName, `${source.getBoundingClientRect().height / zoomOf(source)}px`)
    write()
    const ro = new ResizeObserver(write)
    ro.observe(source)
    return () => {
      ro.disconnect()
      target.style.removeProperty(varName)
    }
  }, [source, target, varName])

  return { source: setSource, target: setTarget }
}

/**
 * Shared sx for pinning an element below the sticky page header inside the Labs
 * scroll container.
 *
 * The `::before` block is load-bearing, not decoration: the scroll container has
 * `p: 6` (24px) of padding, and a sticky element pins to the scrollport edge —
 * above its own padding — so without a solid block covering that strip, content
 * scrolling underneath shows through the gap.
 *
 * ## A sticky element OWNS the gap below it — never the content's `pt` (VRG-560)
 *
 * This is the same argument as the `::before` above, applied to the other edge, and it
 * was reported three separate times as "the gap collapses when you scroll". A gap
 * expressed as `pt` on the following content is FLOW spacing: it positions that content
 * relative to where the sticky element *sits in the document*. A pinned element no longer
 * sits there — the content keeps scrolling and rises until it meets the pinned element's
 * own bottom edge, so a gap that measured 16px at rest measures 0 once it pins. Nothing
 * moved; the spacing was simply never attached to the thing it was spacing away from.
 *
 * So the gap belongs to the STICKY ELEMENT, as its own `padding-bottom`, over its own
 * opaque `background.default`. Then content passing underneath is painted over by the
 * padding and can never come closer than the intended distance, at any scroll offset. The
 * content below carries **no** `pt` at all — if it did, the rest state would be the sum
 * of the two and the gap would grow.
 *
 * Today the only user of this is the Labs page header itself, whose `pb` is
 * `LABS_HEADER_GAP`. The section bands that also relied on it are gone (see
 * `LABS_CONTENT_TOP`) — but the rule is not, because the sections still pin a left column
 * directly below this header, and that column's rest position is set by this padding.
 *
 * One consequence worth knowing before changing that `pb`: it moves the MEASURED height,
 * and that is the point. `--labs-header-h` is published from this element's own
 * `getBoundingClientRect`, so anything pinning below it follows the padding automatically.
 * That self-consistency is precisely what a `::after` cover strip would NOT give — an
 * absolutely-positioned strip paints over the gap without occupying it, so the measured
 * height would stay behind and the columns would pin underneath their own cover.
 */
export const labsStickySx = (top: number | string, zIndex: number) => ({
  position: 'sticky' as const,
  top,
  zIndex,
  bgcolor: 'background.default',
  '&::before': {
    content: '""',
    position: 'absolute' as const,
    left: 0,
    right: 0,
    bottom: '100%',
    height: 80,
    bgcolor: 'background.default',
  },
})
